// Agent-edit "Knowledge" section. Three states it has to handle:
//
//   1. Agent has no KB attached (knowledgeBaseId === null) →
//      "Knowledge base not attached" empty state. We can't auto-create a
//      KB from this surface yet (no POST /v1/knowledge-bases), so the
//      message points at seed:dev / dashboard agent-edit form instead.
//   2. Agent has a KB → list documents + show file picker. Polls every
//      2s while any doc is in a non-terminal status (pending / scanning
//      / parsing / embedding) so the user sees the row flip to ready /
//      failed without a manual refresh.
//   3. Mid-upload → file picker disabled, progress message, errors
//      surfaced inline.
//
// The upload itself is delegated to knowledgeApi.upload (three-step:
// request URL → PUT bytes → confirm). On success we invalidate the
// list query so the new doc appears in the next render.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import type { Agent, KbDocument, KbDocumentStatus } from '@cortex/shared';
import { useApiClient } from '../../lib/providers.js';
import { ApiError, type ApiClient } from '../../lib/api-client.js';
import {
  knowledgeApi,
  knowledgeListQueryKey,
  sha256Hex,
} from '../../lib/api/knowledge.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card.js';
import { Button } from '../../components/ui/button.js';
import { cn } from '../../lib/utils.js';

const NON_TERMINAL: ReadonlySet<KbDocumentStatus> = new Set([
  'pending',
  'scanning',
  'parsing',
  'chunking',
  'embedding',
]);

export function KnowledgeCard({ agent }: { agent: Agent }) {
  if (!agent.knowledgeBaseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Knowledge</CardTitle>
          <CardDescription>
            This agent has no knowledge base attached. Re-run the dev seed
            (<code className="font-mono text-xs">pnpm seed:dev</code>) to
            wire one in, or attach an existing knowledge base via the agent
            form above.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return <Attached agent={agent} />;
}

function Attached({ agent }: { agent: Agent }) {
  const api = useApiClient();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const docs = useQuery({
    queryKey: knowledgeListQueryKey(agent.id),
    queryFn: () => knowledgeApi.list(api, agent.id),
    // Poll every 2s if any doc is mid-ingestion. The list goes static
    // again the moment everything settles to ready / failed.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const stillCooking = data.documents.some((d) => NON_TERMINAL.has(d.status));
      return stillCooking ? 2_000 : false;
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const sha = await sha256Hex(file);
      return knowledgeApi.upload(api, agent.id, file, sha);
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: knowledgeListQueryKey(agent.id) });
      if (fileRef.current) fileRef.current.value = '';
    },
    onError: (err) => {
      setError(formatUploadError(err));
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Knowledge</CardTitle>
        <CardDescription>
          Upload documents to this agent's knowledge base. Plain text, markdown,
          and PDF up to 20 MB.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/*
          The whole block is a drop zone so a tenant can drag a file
          straight onto the card. preventDefault on dragover/drop is
          mandatory — without it the browser navigates to the file (the
          default action for an unhandled drop), which feels like a bug.
        */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!upload.isPending) setDragOver(true);
          }}
          onDragLeave={() => { setDragOver(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file && !upload.isPending) upload.mutate(file);
          }}
          className={cn(
            'flex items-center gap-3 rounded-md border border-dashed p-3 transition-colors',
            dragOver ? 'border-primary bg-accent/50' : 'border-input',
            upload.isPending && 'opacity-60',
          )}
        >
          <input
            ref={fileRef}
            type="file"
            aria-label="Document to upload"
            disabled={upload.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
            }}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
          />
          {upload.isPending ? (
            <span className="shrink-0 text-xs text-muted-foreground">Uploading…</span>
          ) : (
            <span className="shrink-0 text-xs text-muted-foreground">or drop here</span>
          )}
        </div>
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {docs.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {docs.isError && (
          <p className="text-sm text-destructive">
            {docs.error instanceof Error ? docs.error.message : 'failed to load documents'}
          </p>
        )}
        {docs.data && (
          <DocumentList documents={docs.data.documents} agentId={agent.id} api={api} />
        )}
      </CardContent>
    </Card>
  );
}

interface DocumentListProps {
  documents: readonly KbDocument[];
  agentId: string;
  api: ApiClient;
}

function DocumentList({ documents, agentId, api }: DocumentListProps) {
  if (documents.length === 0) {
    return <p className="text-sm text-muted-foreground">No documents yet.</p>;
  }
  return (
    <ul className="divide-y rounded-md border">
      {documents.map((d) => (
        <DocumentRow key={d.id} document={d} agentId={agentId} api={api} />
      ))}
    </ul>
  );
}

function DocumentRow({
  document,
  agentId,
  api,
}: {
  document: KbDocument;
  agentId: string;
  api: ApiClient;
}) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => knowledgeApi.remove(api, agentId, document.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: knowledgeListQueryKey(agentId) });
    },
  });

  return (
    <li className="flex items-center justify-between gap-4 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{document.filename}</div>
        <div className="text-xs text-muted-foreground">
          {formatBytes(document.bytes)} · {document.mimeType}
          {document.error ? ` · ${document.error}` : ''}
        </div>
        {remove.isError && (
          <p className="mt-1 text-xs text-destructive" role="alert">
            {remove.error instanceof Error ? remove.error.message : 'delete failed'}
          </p>
        )}
      </div>
      <StatusBadge status={document.status} />
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Delete ${document.filename}`}
        disabled={remove.isPending}
        onClick={() => {
          // Skipping a confirm dialog here — for the MVP we lean on the
          // delete being cheap to redo (re-upload). If the surface
          // matters more, swap for a Radix Alert Dialog.
          remove.mutate();
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </li>
  );
}

function StatusBadge({ status }: { status: KbDocumentStatus }) {
  // Tailwind needs the literal class strings; keyed map > template string.
  const tone = STATUS_TONES[status];
  return (
    <span
      className={cn(
        'rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
        tone,
      )}
    >
      {status}
    </span>
  );
}

const STATUS_TONES: Record<KbDocumentStatus, string> = {
  pending: 'bg-muted text-muted-foreground',
  scanning: 'bg-amber-100 text-amber-900',
  parsing: 'bg-amber-100 text-amber-900',
  chunking: 'bg-amber-100 text-amber-900',
  embedding: 'bg-amber-100 text-amber-900',
  ready: 'bg-emerald-100 text-emerald-900',
  failed: 'bg-rose-100 text-rose-900',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatUploadError(err: unknown): string {
  if (err instanceof ApiError) {
    return `HTTP ${String(err.status)}${err.code ? ` · ${err.code}` : ''}: ${err.message}`;
  }
  return err instanceof Error ? err.message : 'unknown error';
}
